import { useState } from "react";
import { useCorrelacoesCfopCst, useCstIcmsCombo, useCsosnCombo } from "@/features/fiscal/hooks/useFiscal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { CfopSearchCombobox } from "@/components/CfopSearchCombobox";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import type { CorrelacaoCfopCstListagemDto, CorrelacaoCfopCstDto, SalvarCorrelacaoCfopCstRequest } from "@/features/fiscal/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM: SalvarCorrelacaoCfopCstRequest = {
  cstSaida: "", descricao: "", csosnId: 0, isInativo: false,
  cfopSaida: 0, cfopSaidaNaoContrib: 0, cfopSaidaUf: 0,
  cfopSaidaUfNaoContrib: 0, cfopSaidaExterior: 0, cfopSaidaContaForaConta: 0,
  cfopEntrada: 0, cfopEntradaUf: 0, cfopEntradaExterior: 0,
};

function dtoToForm(d: CorrelacaoCfopCstDto): SalvarCorrelacaoCfopCstRequest {
  return {
    cstSaida: d.cstSaida ?? "", descricao: d.descricao ?? "", csosnId: d.csosnId, isInativo: d.isInativo,
    cfopSaida: d.cfopSaida, cfopSaidaNaoContrib: d.cfopSaidaNaoContrib,
    cfopSaidaUf: d.cfopSaidaUf, cfopSaidaUfNaoContrib: d.cfopSaidaUfNaoContrib,
    cfopSaidaExterior: d.cfopSaidaExterior, cfopSaidaContaForaConta: d.cfopSaidaContaForaConta,
    cfopEntrada: d.cfopEntrada, cfopEntradaUf: d.cfopEntradaUf, cfopEntradaExterior: d.cfopEntradaExterior,
  };
}

function CfopNumInput({ label, value, onChange }: {
  label: string; value: number; onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <CfopSearchCombobox value={value || null} onChange={v => onChange(v ?? 0)} />
    </div>
  );
}

function CodigoSelect({ value, options, onChange }: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  const opts = value && !options.some(o => o.value === value) ? [{ value, label: value }, ...options] : options;
  return (
    <Select value={value || "__none__"} onValueChange={v => onChange(v === "__none__" ? "" : v)}>
      <SelectTrigger className="font-mono"><SelectValue placeholder="Selecione..." /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">— Nenhum —</SelectItem>
        {opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function CorrelacaoCfopCst() {
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const { query, obterPorId, criar, atualizar, excluir } = useCorrelacoesCfopCst(busca || undefined, pagina, tamanhoPagina);
  const itens: CorrelacaoCfopCstListagemDto[] = query.data?.itens ?? [];

  const { data: cstIcmsList = [] } = useCstIcmsCombo();
  const { data: csosnList = [] }    = useCsosnCombo();
  const icmsOpts = cstIcmsList
    .filter(c => c.cst && c.cst.length <= 2)   // CST ICMS tradicional (2 dígitos); IBS/CBS tem 3
    .map(c => ({ value: c.cst!, label: `${c.cst} — ${c.descricao ?? ""}` }));
  const csosnOpts = csosnList.map(c => ({ id: c.id, label: `${c.codigoCsosn} — ${c.descricao ?? ""}` }));

  const totalItens = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, totalItens);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [excluirId,  setExcluirId]  = useState<number | null>(null);
  const [editingId,  setEditingId]  = useState<number | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [form,       setForm]       = useState<SalvarCorrelacaoCfopCstRequest>(EMPTY_FORM);

  const set = <K extends keyof SalvarCorrelacaoCfopCstRequest>(k: K, v: SalvarCorrelacaoCfopCstRequest[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  const openNew = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };

  const openEdit = async (id: number) => {
    setLoading(true);
    try { const dto = await obterPorId(id); setEditingId(id); setForm(dtoToForm(dto)); setDialogOpen(true); }
    catch { toast.error("Não foi possível carregar a correlação."); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.cstSaida.trim()) { toast.error("CST de saída é obrigatório."); return; }
    try {
      if (editingId) {
        await atualizar.mutateAsync({ id: editingId, req: form });
        toast.success("Correlação atualizada.");
      } else {
        await criar.mutateAsync(form);
        toast.success("Correlação criada.");
      }
      setDialogOpen(false);
    } catch { /* global toast */ }
  };

  const handleExcluir = async () => {
    if (!excluirId) return;
    try { await excluir.mutateAsync(excluirId); toast.success("Excluída com sucesso."); }
    catch { /* global toast */ }
    setExcluirId(null);
  };

  const isSaving = criar.isPending || atualizar.isPending;
  if (query.isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ArrowLeftRight className="h-6 w-6" /> Correlação CFOP / CST
          </h1>
          <p className="text-muted-foreground">Mapeamento de CST para CFOPs por cenário de operação</p>
        </div>
        <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Nova</Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar por CST ou descrição..." className="pl-9" value={busca} onChange={e => { setBusca(e.target.value); setPagina(1); }} />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[90px]">CST Saída</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[90px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {itens.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-mono font-semibold">{c.cstSaida ?? "—"}</TableCell>
                <TableCell>{c.descricao ?? "—"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={c.isInativo ? "secondary" : "success"}>
                    {c.isInativo ? "Inativa" : "Ativa"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c.id)} disabled={loading}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setExcluirId(c.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {itens.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  Nenhuma correlação cadastrada.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={query.data.totalPaginas}
          totalItens={totalItens}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={setTamanhoPagina}
        />
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Correlação" : "Nova Correlação"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label>CST ICMS Saída *</Label>
                <CodigoSelect value={form.cstSaida} options={icmsOpts} onChange={v => set("cstSaida", v)} />
              </div>
              <div className="space-y-1">
                <Label>CSOSN (Simples)</Label>
                <Select value={form.csosnId ? String(form.csosnId) : "0"} onValueChange={v => set("csosnId", Number(v))}>
                  <SelectTrigger className="font-mono"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">— Nenhum —</SelectItem>
                    {csosnOpts.map(o => <SelectItem key={o.id} value={String(o.id)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="descricao">Descrição</Label>
                <Input id="descricao" maxLength={100} value={form.descricao} onChange={e => set("descricao", e.target.value)} />
              </div>
            </div>

            <div>
              <p className="text-sm font-medium mb-3 text-muted-foreground uppercase tracking-wide">CFOPs de Saída</p>
              <div className="grid grid-cols-2 gap-3">
                <CfopNumInput label="Contrib. mesmo estado"       value={form.cfopSaida}              onChange={v => set("cfopSaida", v)} />
                <CfopNumInput label="Não contrib. mesmo estado"   value={form.cfopSaidaNaoContrib}    onChange={v => set("cfopSaidaNaoContrib", v)} />
                <CfopNumInput label="Contrib. outro estado"       value={form.cfopSaidaUf}            onChange={v => set("cfopSaidaUf", v)} />
                <CfopNumInput label="Não contrib. outro estado"   value={form.cfopSaidaUfNaoContrib}  onChange={v => set("cfopSaidaUfNaoContrib", v)} />
                <CfopNumInput label="Exterior"                    value={form.cfopSaidaExterior}      onChange={v => set("cfopSaidaExterior", v)} />
                <CfopNumInput label="Conta e Ordem"               value={form.cfopSaidaContaForaConta} onChange={v => set("cfopSaidaContaForaConta", v)} />
              </div>
            </div>

            <div>
              <p className="text-sm font-medium mb-3 text-muted-foreground uppercase tracking-wide">CFOPs de Entrada</p>
              <div className="grid grid-cols-3 gap-3">
                <CfopNumInput label="Mesmo estado"  value={form.cfopEntrada}         onChange={v => set("cfopEntrada", v)} />
                <CfopNumInput label="Outro estado"  value={form.cfopEntradaUf}       onChange={v => set("cfopEntradaUf", v)} />
                <CfopNumInput label="Exterior"       value={form.cfopEntradaExterior} onChange={v => set("cfopEntradaExterior", v)} />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch id="isInativo" checked={form.isInativo} onCheckedChange={v => set("isInativo", v)} />
              <Label htmlFor="isInativo">Inativa</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSaving}>{isSaving ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!excluirId} onOpenChange={() => setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir correlação</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleExcluir} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
